/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { apiClient } from '@lib/api-client';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { IApiError, TParams } from '@global-types/index';
import { queryClient } from '@lib/react-query';
import useStore from '@store/index';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */

type TApiResponse = { id: string; active: boolean };

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const activateAdditionalityProject = (data: {
	active: boolean;
	ids: string[];
}): Promise<TApiResponse> => {
	return apiClient.patch(
		`/additionalities/activate`,
		data
	);
};

export const useActivateAdditionalityProject = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { t } = useTranslation();
	const {
		setAdditionalitiesSelectedRow,
		additionalitiesSelectedRow,
	} = useStore();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: activateAdditionalityProject,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					'databases-additionalities',
					{
						page: page,
						size: size,
						queryParams: queryParams,
						sortParams: sortParams,
					},
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					'additionality-projects',
					{
						page: page,
						size: size,
						queryParams: queryParams,
					},
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					'additionality-projects',
					additionalitiesSelectedRow?.id,
				],
			});

			setAdditionalitiesSelectedRow({
				...additionalitiesSelectedRow,
				active: !additionalitiesSelectedRow.active,
			});
			notifications.show({
				title: 'Success',
				message: t(
					'notifications.statusProjectSuccessfullyUpdated'
				),
				color: 'var(--mantine-color-igluGreen-9)',
				autoClose: 1500,
			});
		},
		onError: (error: IApiError) => {
			notifications.show({
				title: t('global.error'),
				message: t(`api.${error.type}`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
